<template>
  <div class="opentok-setup-form">
    <div
      class="form-group camera-select d-flex flex-row align-items-center justify-content-between mt-3"
    >
      <span class="label"
        ><i class="fal fa-video c-coral mr-2"></i
        >{{ $t('GENERAL.LABEL.VIDEO') }}</span
      >
      <div
        class="d-flex flex-row align-items-center justify-content-end flex-grow-1"
      >
        <b-form-select
          class="mr-2"
          :value="streamingProps.selectedVideoDevice"
          :disabled="!streamingProps.videoEnabled"
          @input="
            $store.dispatch('setting/setStreamingProps', {
              selectedVideoDevice: $event,
            })
          "
        >
          <b-form-select-option
            v-for="device in availableVideoDevices"
            :key="device.deviceId"
            :value="device"
          >
            {{ device.label }}
          </b-form-select-option>
        </b-form-select>
        <div class="right-container toggle-video">
          <HMSwitch
            class="mw-110"
            :value="streamingProps.videoEnabled"
            @input="
              $store.dispatch('setting/setStreamingProps', {
                videoEnabled: $event,
              })
            "
          />
        </div>
      </div>
    </div>
    <div
      class="form-group audio-input-select d-flex flex-row align-items-center justify-content-between"
    >
      <span class="label"
        ><i class="fal fa-microphone c-coral mr-2"></i
        >{{ $t('GENERAL.LABEL.AUDIO') }}</span
      >
      <div
        class="d-flex flex-row align-items-start justify-content-end flex-grow-1"
      >
        <div class="mr-2 w-100">
          <b-form-select
            :value="streamingProps.selectedAudioInputDevice"
            :disabled="!streamingProps.audioInputEnabled"
            @input="
              $store.dispatch('setting/setStreamingProps', {
                selectedAudioInputDevice: $event,
              })
            "
          >
            <b-form-select-option
              v-for="device in availableAudioInputDevices"
              :key="device.deviceId"
              :value="device"
            >
              {{ device.label }}
            </b-form-select-option>
          </b-form-select>
          <div class="w-100 bg-lightest-gray rounded">
            <div
              id="mic-volume"
              v-if="streamingProps.audioInputEnabled"
              class="mt-1 rounded mic-volume"
              :style="`width:${volume}%;`"
            ></div>
          </div>
        </div>
        <div class="right-container toggle-audio">
          <HMSwitch
            :value="streamingProps.audioInputEnabled"
            @input="
              $store.dispatch('setting/setStreamingProps', {
                audioInputEnabled: $event,
              })
            "
          />
        </div>
      </div>
    </div>
    <div
      class="form-group audio-output-select d-flex flex-row align-items-center justify-content-between"
    >
      <span class="label"
        ><i class="fal fa-volume-up c-coral mr-2"></i
        >{{ $t('GENERAL.LABEL.OUTPUT') }}</span
      >
      <div
        class="d-flex flex-row align-items-center justify-content-end flex-grow-1"
      >
        <b-form-select
          class="mr-2"
          :value="streamingProps.selectedAudioOutputDevice"
          :disabled="!availableAudioOutputDevicesEnabled"
          @input="
            $store.dispatch('setting/setStreamingProps', {
              selectedAudioOutputDevice: $event,
            })
          "
        >
          <b-form-select-option
            v-for="device in availableAudioOutputDevices"
            :key="device.deviceId"
            :value="device"
          >
            {{ device.label }}
          </b-form-select-option>
        </b-form-select>
        <div class="right-container">
          <span class="play-btn" @click="playSound">
            <i class="fal fa-play-circle c-coral mr-2"></i
            >{{ $t('GENERAL.LABEL.PLAY') }}
          </span>
        </div>
      </div>
    </div>
    <div
      class="form-group advanced-audio-processing-select d-flex flex-row align-items-center justify-content-between"
    >
      <span class="label">
        <i class="fal fa-waveform-path c-coral mr-2"></i>
        {{ $t('EVENTS.LABEL.ADVANCED_AUDIO_PROCESSING') }}
      </span>
      <div
        class="d-flex flex-row align-items-center justify-content-end flex-grow-1"
      >
        <div class="right-container">
          <HMSwitch
            class="mw-110"
            :value="streamingProps.advancedAudioProcessing"
            @input="
              $store.dispatch('setting/setStreamingProps', {
                advancedAudioProcessing: $event,
              })
            "
          />
        </div>
      </div>
    </div>
    <div
      class="form-group contain-mode-select d-flex flex-row align-items-center justify-content-between"
    >
      <span class="label"
        ><i class="fal fa-desktop c-coral mr-2"></i
        >{{ $t('EVENTS.LABEL.FIT_MODE') }}</span
      >
      <div
        class="d-flex flex-row align-items-center justify-content-end flex-grow-1"
      >
        <div class="right-container">
          <HMToggleButtonGroup
            class="mw-110"
            :value="streamingProps.useContainMode"
            @input="
              $store.dispatch('setting/setStreamingProps', {
                useContainMode: $event,
              })
            "
          >
            <template slot="falseState">
              <span>
                {{ $t('EVENTS.LABEL.COVER') }}
              </span>
            </template>
            <template slot="trueState">
              <span>
                {{ $t('EVENTS.LABEL.CONTAIN') }}
              </span>
            </template>
          </HMToggleButtonGroup>
        </div>
      </div>
    </div>
    <div
      class="form-group connectivity-test d-flex flex-row align-items-center justify-content-between"
    >
      <span class="mr-4"
        ><i class="fal fa-wifi c-coral mr-2"></i
        >{{ $t('GENERAL.LABEL.CONNECTIVITY') }}</span
      >
      <div class="d-flex flex-row align-items-center">
        <div class="right-container">
          <span class="play-btn" @click="testConnectivity">
            <i class="fal fa-chart-bar c-coral mr-2"></i
            >{{ $t('GENERAL.LABEL.RUN_TEST') }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import HMToggleButtonGroup from '@/web/components/common/HMToggleButtonGroup';
import { mapGetters } from 'vuex';

export default {
  name: 'OpenTokHWSetupForm',
  components: { HMToggleButtonGroup },
  props: [
    'stream',
    'availableVideoDevices',
    'availableAudioInputDevices',
    'availableAudioOutputDevices',
  ],
  computed: {
    ...mapGetters({
      streamingProps: 'setting/streamingProps',
    }),
    availableAudioOutputDevicesEnabled() {
      return this.availableAudioOutputDevices.length > 0;
    },
  },
  data() {
    return {
      volume: 0,
      audioContext: null,
      analyser: null,
      microphone: null,
      scriptProcessor: null,
    };
  },
  methods: {
    selectStreamDevices() {
      // selects default devices in comboboxes
      if (!this.streamingProps.selectedVideoDevice) {
        let selectedVideoDevice = this.availableVideoDevices.find(video => {
          const tracks = this.stream.getVideoTracks();
          if (tracks && tracks.length)
            return tracks[0].label.indexOf(video.label) === 0;
          else return false;
        });
        if (!selectedVideoDevice && this.availableVideoDevices.length) {
          selectedVideoDevice = this.availableVideoDevices[0];
        }
        console.log('selectedVideoDevice=', selectedVideoDevice);
        if (selectedVideoDevice) {
          this.$store.dispatch('setting/setStreamingProps', {
            selectedVideoDevice,
          });
        }
      }
      if (!this.streamingProps.selectedAudioInputDevice) {
        let selectedAudioInputDevice = this.availableAudioInputDevices.find(
          audio => {
            const tracks = this.stream.getAudioTracks();
            if (tracks && tracks.length)
              return tracks[0].label.indexOf(audio.label === audio.label);
            else return false;
          }
        );
        if (
          !selectedAudioInputDevice &&
          this.availableAudioInputDevices.length
        ) {
          selectedAudioInputDevice = this.availableAudioInputDevices[0];
        }
        console.log('selectedAudioInputDevice=', selectedAudioInputDevice);
        if (selectedAudioInputDevice) {
          this.$store.dispatch('setting/setStreamingProps', {
            selectedAudioInputDevice,
          });
        }
      }
      if (
        !this.streamingProps.selectedAudioOutputDevice &&
        this.availableAudioOutputDevices.length > 0
      ) {
        const selectedAudioOutputDevice = this.availableAudioOutputDevices[0];
        console.log('selectedAudioOutputDevice=', selectedAudioOutputDevice);
        if (selectedAudioOutputDevice) {
          this.$store.dispatch('setting/setStreamingProps', {
            selectedAudioOutputDevice,
          });
        }
      }
    },
    playSound() {
      const audioFile = '/assets/audio/audio-tone.mp3';
      const audio = document.createElement('audio');
      audio.style.display = 'none';
      audio.src = audioFile;
      audio.autoplay = true;
      audio.setSinkId(this.streamingProps.selectedAudioOutputDevice.deviceId);
      audio.onended = function () {
        audio.remove(); //Remove when played.
      };
      document.body.appendChild(audio);
    },
    testConnectivity() {
      window.open('https://tokbox.com/developer/tools/precall/');
    },
    setupAudioVolumeProcessing() {
      if (!this.stream) return;
      if (this.audioContext != null) {
        this.audioContext.close();
        this.analyser.disconnect();
        this.microphone.disconnect();
        this.scriptProcessor.disconnect();
      }
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      this.audioContext = new AudioContext();
      this.analyser = this.audioContext.createAnalyser();
      this.microphone = this.audioContext.createMediaStreamSource(this.stream);
      this.scriptProcessor = this.audioContext.createScriptProcessor(
        2048,
        1,
        1
      );

      this.analyser.smoothingTimeConstant = 0.8;
      this.analyser.fftSize = 1024;

      this.microphone.connect(this.analyser);
      this.analyser.connect(this.scriptProcessor);
      this.scriptProcessor.connect(this.audioContext.destination);
      this.scriptProcessor.onaudioprocess = () => {
        const array = new Uint8Array(this.analyser.frequencyBinCount);
        this.analyser.getByteFrequencyData(array);
        let values = 0;

        const length = array.length;
        for (let i = 0; i < length; i++) {
          values += array[i];
        }

        const average = values / length;
        try {
          this.volume = average;
          // eslint-disable-next-line no-empty
        } catch (e) {}
      };
    },
  },
  mounted() {
    this.selectStreamDevices();
    this.setupAudioVolumeProcessing();
  },
  watch: {
    stream(value) {
      this.selectStreamDevices();
      this.setupAudioVolumeProcessing();
    },
    availableVideoDevices(value) {
      this.selectStreamDevices();
    },
    availableAudioInputDevices(value) {
      this.selectStreamDevices();
    },
    availableAudioOutputDevices(value) {
      this.selectStreamDevices();
    },
    'streamingProps.selectedAudioInputDevice': {
      handler(value) {
        // wait until the change is applied to tokbox stream
        setTimeout(this.setupAudioVolumeProcessing, 200);
      },
    },
  },
};
</script>

<style lang="scss">
@import '@/web/styles/components-variables.scss';

.opentok-setup-form {
  width: auto;
  max-width: 100%;
  margin-top: 30px;

  .label {
    min-width: 105px;
  }

  .right-container {
    min-width: 120px;
  }

  .v-select {
    min-width: 250px;
    max-width: 100%;
    width: 100%;
    font-size: 12px;

    .vs__dropdown-toggle .vs__clear {
      display: none;
    }

    @media screen and (max-width: 768px) {
      min-width: 100px;
    }

    .vs__dropdown-toggle {
      height: 46px;
    }
  }

  .mic-volume {
    height: 6px;
    background-color: $mustard;
    background-image: linear-gradient(to right, $mustard, $brown);
  }

  .play-btn,
  .test-btn {
    white-space: nowrap;
    cursor: pointer;
    padding: 0.5rem 1rem;
    border-radius: 6px;

    &:hover {
      background-color: $medium-gray;
    }
  }
}
</style>
